/**
 * Running a local relay server will allow you to hide your API key
 * and run custom logic on the server
 *
 * Set the local relay server address to:
 * REACT_APP_LOCAL_RELAY_SERVER_URL=http://localhost:8081
 *
 * This will also require you to set OPENAI_API_KEY= in a `.env` file
 * You can run it with `npm run relay`, in parallel with `npm start`
 */
const LOCAL_RELAY_SERVER_URL: string =
  process.env.REACT_APP_LOCAL_RELAY_SERVER_URL || '';

import { useEffect, useRef, useCallback, useState } from 'react';

import { RealtimeClient } from '@openai/realtime-api-beta';
import { ItemType } from '@openai/realtime-api-beta/dist/lib/client.js';
import { WavRecorder, WavStreamPlayer } from '../lib/wavtools/index.js';
import { instructions } from '../utils/conversation_config.js';
import { WavRenderer } from '../utils/wav_renderer';
import ReactMarkdown from 'react-markdown';


import { X, Zap } from 'react-feather';
import { Button } from '../components/button/Button';
import { Toggle } from '../components/toggle/Toggle';

import './ConsolePage.scss';
import segmentData from '../../../../pages/pages/assistant/data/segment_percent.json';
import productData from '../../../../pages/pages/assistant/data/product_percent.json';
import segmentProduct from '../../../../pages/pages/assistant/data/segment_product_percent.json';
import productSegment from '../../../../pages/pages/assistant/data/product_segment_percent.json';
import sessionData from '../../../../pages/pages/assistant/data/session_data.json';
import segmentProductPercentTimeSeries from '../../../pages/assistant/data/segment_product_percent_time_series.json'
import productSegmentPercentTimeSeries from '../../../pages/assistant/data/product_segment_percent_time_series.json'


interface SegmentProductTimeSeriesItem {
  profile_name: string;
  log_date: string;
  product_name: string;
  percentage: number;
}
const segmentProductTimeSeries = segmentProductPercentTimeSeries as SegmentProductTimeSeriesItem[];





/**
 * Type for all event logs
 */
interface RealtimeEvent {
  time: string;
  source: 'client' | 'server';
  count?: number;
  event: { [key: string]: any };
}

export function ConsolePage() {
  /**
   * Ask user for API Key
   * If we're using the local relay server, we don't need this
   */
  const apiKey = LOCAL_RELAY_SERVER_URL
    ? ''
    : localStorage.getItem('tmp::voice_api_key') ||
    prompt('OpenAI API Key') ||
    '';
  if (apiKey !== '') {
    localStorage.setItem('tmp::voice_api_key', apiKey);
  }

  /**
   * Instantiate:
   * - WavRecorder (speech input)
   * - WavStreamPlayer (speech output)
   * - RealtimeClient (API client)
   */
  const wavRecorderRef = useRef<WavRecorder>(
    new WavRecorder({ sampleRate: 24000 })
  );
  const wavStreamPlayerRef = useRef<WavStreamPlayer>(
    new WavStreamPlayer({ sampleRate: 24000 })
  );
  const clientRef = useRef<RealtimeClient>(
    new RealtimeClient(
      LOCAL_RELAY_SERVER_URL
        ? { url: LOCAL_RELAY_SERVER_URL }
        : {
          apiKey: apiKey,
          dangerouslyAllowAPIKeyInBrowser: true,
        }
    )
  );

  /**
   * References for
   * - Rendering audio visualization (canvas)
   * - Autoscrolling event logs
   * - Timing delta for event log displays
   */
  const clientCanvasRef = useRef<HTMLCanvasElement>(null);
  const serverCanvasRef = useRef<HTMLCanvasElement>(null);
  const eventsScrollHeightRef = useRef(0);
  const eventsScrollRef = useRef<HTMLDivElement>(null);
  const startTimeRef = useRef<string>(new Date().toISOString());

  const [isTitleVisible, setIsTitleVisible] = useState(true);


  /**
   * All of our variables for displaying application state
   * - items are all conversation items (dialog)
   * - realtimeEvents are event logs, which can be expanded
   * - memoryKv is for set_memory() function
   * - coords, marker are for get_weather() function
   */
  const [items, setItems] = useState<ItemType[]>([]);
  const [realtimeEvents, setRealtimeEvents] = useState<RealtimeEvent[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [canPushToTalk, setCanPushToTalk] = useState(true);
  const [isRecording, setIsRecording] = useState(false);

  /**
   * Connect to conversation:
   * WavRecorder taks speech input, WavStreamPlayer output, client is API client
   */
  const connectConversation = useCallback(async () => {
    setRealtimeEvents([]);
    setItems([]);
    const client = clientRef.current;
    const wavRecorder = wavRecorderRef.current;
    const wavStreamPlayer = wavStreamPlayerRef.current;

    // Set state variables
    startTimeRef.current = new Date().toISOString();
    setIsConnected(true);
    setIsTitleVisible(false); // Başlık gizlenir
    setRealtimeEvents([]);
    setItems(client.conversation.getItems());

    // Connect to microphone
    await wavRecorder.begin();

    // Connect to audio output
    await wavStreamPlayer.connect();

    // Connect to realtime API
    await client.connect();

    if (client.getTurnDetectionType() === 'server_vad') {
      await wavRecorder.record((data) => client.appendInputAudio(data.mono));
    }
  }, []);

  /**
   * Disconnect and reset conversation state
   */
  const disconnectConversation = useCallback(async () => {
    setIsConnected(false);

    const client = clientRef.current;
    client.disconnect();

    const wavRecorder = wavRecorderRef.current;
    await wavRecorder.end();

    const wavStreamPlayer = wavStreamPlayerRef.current;
    await wavStreamPlayer.interrupt();
  }, []);

  /**
   * In push-to-talk mode, start recording
   * .appendInputAudio() for each sample
   */
  const startRecording = async () => {
    setIsRecording(true);
    const client = clientRef.current;
    const wavRecorder = wavRecorderRef.current;
    const wavStreamPlayer = wavStreamPlayerRef.current;

    if (!client.isConnected()) {
      await client.connect(); // Bağlantı yoksa yeniden bağlan
    }

    const trackSampleOffset = await wavStreamPlayer.interrupt();
    if (trackSampleOffset?.trackId) {
      const { trackId, offset } = trackSampleOffset;
      await client.cancelResponse(trackId, offset);
    }
    if (wavRecorder.getStatus() !== 'recording') {
      await wavRecorder.record((data) => client.appendInputAudio(data.mono));
    }
  };

  /**
   * In push-to-talk mode, stop recording
   */
  const stopRecording = async () => {
    setIsRecording(false);
    const client = clientRef.current;
    const wavRecorder = wavRecorderRef.current;
    await wavRecorder.pause();
    client.createResponse();
  };

  /**
   * Switch between Manual <> VAD mode for communication
   */
  const changeTurnEndType = async (value: string) => {
    const client = clientRef.current;
    const wavRecorder = wavRecorderRef.current;
    if (value === 'none' && wavRecorder.getStatus() === 'recording') {
      await wavRecorder.pause();
    }
    client.updateSession({
      turn_detection: value === 'none' ? null : { type: 'server_vad' },
    });
    if (value === 'server_vad' && client.isConnected()) {
      await wavRecorder.record((data) => client.appendInputAudio(data.mono));
    }
    setCanPushToTalk(value === 'none');
  };

  /**
   * Auto-scroll the event logs
   */
  useEffect(() => {
    if (eventsScrollRef.current) {
      const eventsEl = eventsScrollRef.current;
      const scrollHeight = eventsEl.scrollHeight;
      // Only scroll if height has just changed
      if (scrollHeight !== eventsScrollHeightRef.current) {
        eventsEl.scrollTop = scrollHeight;
        eventsScrollHeightRef.current = scrollHeight;
      }
    }
  }, [realtimeEvents]);

  /**
   * Auto-scroll the conversation logs
   */
  useEffect(() => {
    const conversationEls = [].slice.call(
      document.body.querySelectorAll('[data-conversation-content]')
    );
    for (const el of conversationEls) {
      const conversationEl = el as HTMLDivElement;
      conversationEl.scrollTop = conversationEl.scrollHeight;
    }
  }, [items]);

  /**
   * Set up render loops for the visualization canvas
   */
  useEffect(() => {
    let isLoaded = true;

    const wavRecorder = wavRecorderRef.current;
    const clientCanvas = clientCanvasRef.current;
    let clientCtx: CanvasRenderingContext2D | null = null;

    const wavStreamPlayer = wavStreamPlayerRef.current;
    const serverCanvas = serverCanvasRef.current;
    let serverCtx: CanvasRenderingContext2D | null = null;

    const render = () => {
      if (isLoaded) {
        if (clientCanvas) {
          if (!clientCanvas.width || !clientCanvas.height) {
            clientCanvas.width = clientCanvas.offsetWidth;
            clientCanvas.height = clientCanvas.offsetHeight;
          }
          clientCtx = clientCtx || clientCanvas.getContext('2d');
          if (clientCtx) {
            clientCtx.clearRect(0, 0, clientCanvas.width, clientCanvas.height);
            const result = wavRecorder.recording
              ? wavRecorder.getFrequencies('voice')
              : { values: new Float32Array([0]) };
            WavRenderer.drawBars(
              clientCanvas,
              clientCtx,
              result.values,
              '#0099ff',
              10,
              0,
              8
            );
          }
        }
        if (serverCanvas) {
          if (!serverCanvas.width || !serverCanvas.height) {
            serverCanvas.width = serverCanvas.offsetWidth;
            serverCanvas.height = serverCanvas.offsetHeight;
          }
          serverCtx = serverCtx || serverCanvas.getContext('2d');
          if (serverCtx) {
            serverCtx.clearRect(0, 0, serverCanvas.width, serverCanvas.height);
            const result = wavStreamPlayer.analyser
              ? wavStreamPlayer.getFrequencies('voice')
              : { values: new Float32Array([0]) };
            WavRenderer.drawBars(
              serverCanvas,
              serverCtx,
              result.values,
              '#009900',
              10,
              0,
              8
            );
          }
        }
        window.requestAnimationFrame(render);
      }
    };
    render();

    return () => {
      isLoaded = false;
    };
  }, []);

  /**
   * Core RealtimeClient and audio capture setup
   * Set all of our instructions, tools, events and more
   */
  useEffect(() => {
    // Get refs
    const wavStreamPlayer = wavStreamPlayerRef.current;
    const client = clientRef.current;

    // Set instructions
    client.updateSession({ instructions: instructions });
    client.updateSession({ voice: 'alloy' });
    // Set transcription, otherwise we don't get user transcriptions back
    client.updateSession({ input_audio_transcription: { model: 'whisper-1' } });

    // Add tools

    
    client.addTool(
      {
        name: 'get_product_percent_info',
        description: `
    This tool retrieves detailed purchasing statistics for all products. It serves as the foundation for other tools and analysis. 
    It provides:
    
    - product_name: The name of the product.
    - total_count: The total number of purchases for this product.
    - device_id_count: The count of unique devices or users that purchased this product.
    - sales_compared_to_all_sales: The percentage of total sales this product contributes relative to all sales.
    - product_sales_percent_unique_device: The proportion of unique devices that purchased this product compared to all devices.
    
    **Use this tool for all product-related queries** such as:
    1. Fetching general product statistics.
    2. Filtering the most/least sold products.
    3. Analyzing product sales ratios.
    4. Aggregating data based on specific requirements.
    `,
        parameters: {},
      },
      async () => {
        try {
          console.log("get_product_percent_info");

          // Tüm ürün verisini döndür
          return productData;
        } catch (error) {
          console.error('Error processing JSON data:', error);
          throw new Error('Failed to return the product data.');
        }
      }
    );
    
    client.addTool(
      {
        name: 'get_segment_percent_info',
        description: `
This tool provides a detailed overview of the user profiles or segments utilizing the application.
The "name" field represents the segment or profile of the users, offering insights into the demographics or behavior patterns within the app's user base.
The "percent" field indicates the percentage representation of each segment globally, helping to identify the most prevalent user profiles.
The "tr_percent" field reflects the percentage representation specific to Turkey, allowing for analysis tailored to the Turkish audience.
This tool is particularly useful for understanding audience distribution, identifying key user groups, and tailoring strategies to different user segments both globally and within Turkey.
`,
        parameters: {

        },
      },
      async () => {
        try {
          console.log("get_segment_percent_info")
          return segmentData;
        } catch (error) {
          console.error('Error processing JSON data:', error);
          throw new Error('Failed to return the JSON data.');
        }
      }
    );
    

    client.addTool(
      {
        name: 'get_segment_product_percent_time_series_info_by_profile_name',
        description: `
    This tool provides time-series insights into the purchasing behavior of a specific audience segment or profile. It enables detailed analysis of product preferences over time.
    
    It provides:
    - log_date: The date of the data entry in YYYYMMDD format (e.g., 20241130 for November 30, 2024).
    - product_name: The name of the product purchased by the profile.
    - percentage: The proportion of purchases for this product within the specified profile on the given date.
    
    **Use this tool for time-series analysis**, such as:
    1. Tracking product preferences for a specific profile over time.
    2. Analyzing purchasing trends and changes within a profile.
    3. Comparing product performance across different dates.
    
    This tool is particularly useful for identifying trends and tailoring strategies to specific user segments based on temporal data.`,
        parameters: {
          type: 'object',
          properties: {
            profileName: {
              type: 'string',
              description: 'The name of the profile to analyze',
            },
          },
          required: ['profileName'],
        },
      },
      async ({ profileName }: { [key: string]: any }) => {
        try {
          console.log("get_segment_product_percent_time_series_info_by_profile_name");
          console.log(typeof segmentProductTimeSeries);

          const filteredData = (segmentProductTimeSeries as SegmentProductTimeSeriesItem[])
          .filter((item) => item.profile_name === profileName)
          .map((item) => ({
            log_date: item.log_date,
            product_name: item.product_name,
            percentage: item.percentage,
          }));


          console.log(filteredData);
          return filteredData; // Return filtered time-series data
        } catch (error) {
          console.error('Error processing time-series data:', error);
          throw new Error('Failed to return the time-series data.');
        }
      }
    );

    client.addTool(
      {
        name: 'get_product_segment_percent_time_series_info_by_product_name',
        description: `
    This tool provides time-series insights into the audience profiles or segments associated with a specific product. It enables detailed analysis of segment distribution over time.
    
    It provides:
    - log_date: The date of the data entry in YYYYMMDD format (e.g., 20241130 for November 30, 2024).
    - profile_name: The name of the audience profile or segment.
    - percentage: The proportion of the segment associated with the specified product on the given date.
    
    **Use this tool for time-series analysis**, such as:
    1. Tracking segment preferences for a specific product over time.
    2. Analyzing changes in audience behavior for a product.
    3. Comparing segment distributions across different dates.
    
    This tool is particularly useful for understanding audience-product affinity trends and tailoring marketing strategies accordingly.`,
        parameters: {
          type: 'object',
          properties: {
            productName: {
              type: 'string',
              description: 'The name of the product to analyze',
            },
          },
          required: ['productName'],
        },
      },
      async ({ productName }: { [key: string]: any }) => {
        try {
          console.log("get_product_segment_percent_time_series_info_by_product_name");
          console.log(productName);
          console.log(typeof productSegmentPercentTimeSeries);

          const filteredData = productSegmentPercentTimeSeries
            .filter((item) => item.product_name === productName)
            .map((item) => ({
              log_date: item.log_date,
              profile_name: item.profile_name,
              percentage: item.percentage,
            }));

          console.log(filteredData);
          return filteredData; // Return the filtered time-series data
        } catch (error) {
          console.error('Error processing time-series data:', error);
          throw new Error('Failed to return the time-series data.');
        }
      }
    );
    

    client.addTool(
      {
        name: 'get_segment_product_percent_info_by_profile_name',
        description: `
This tool provides insights into the purchasing behavior of a specific audience segment or profile. It is designed to analyze product preferences for a given profile, and provides:

- product_name: The name of the product purchased by the profile.
- percentage: The proportion of purchases for this product within the specified profile.

Use this tool when you have a specific "profileName" and want to understand the product preferences and purchasing trends of users within that profile. This is ideal for targeted audience analysis.        `
        ,
        parameters: {
          type: 'object',
          properties: {
            profileName: {
              type: 'string',
              description: 'Name of the profile',
            },
          },
          required: ['profileName'],
        },
      },
      async ({ profileName }: { [key: string]: any }) => {
        try {
          console.log("get_segment_product_percent_info_by_profile_name")
          console.log(profileName)
          const filteredData = segmentProduct
            .filter((item) => item.profile_name === profileName)
            .map((item) => ({
              product_name: item.product_name,
              percentage: item.percentage,
            }));

          console.log(filteredData);
          // Sonuçları döndür
          return filteredData;
        } catch (error) {
          console.error('Error processing JSON data:', error);
          throw new Error('Failed to return the JSON data.');
        }
      }
    );

    client.addTool(
      {
        name: 'get_product_segment_percent_info_by_product_name',
        description: `
This retrieves the profile or audience or segment distribution and their corresponding percentage values for a specified product. By providing a product_name, you can analyze the audience profiles associated with the selected product and understand the proportional representation of each profile within the context of that product. This tool is particularly valuable for identifying which audience segments are most aligned with a product, enabling data-driven insights into consumer behavior and product affinity.        `
        ,
        parameters: {
          type: 'object',
          properties: {
            productName: {
              type: 'string',
              description: 'Name of the product name',
            },
          },
          required: ['productName'],
        },
      },
      async ({ productName }: { [key: string]: any }) => {
        try {

          console.log("get_product_segment_percent_info_by_product_name")
          console.log(productName)
          const filteredData = productSegment
            .filter((item) => item.product_name === productName)
            .map((item) => ({
              profile_name: item.profile_name,
              percentage: item.percentage,
            }));


          console.log(filteredData);
          // Sonuçları döndür
          return filteredData;
        } catch (error) {
          console.error('Error processing JSON data:', error);
          throw new Error('Failed to return the JSON data.');
        }
      }
    );



    client.addTool(
      {
        name: 'get_session_info',
        description: `
            This tool retrieves detailed session statistics for all categories based on session data. It provides:
            
            - log_date: The date of the log entry. Date format is YYYY-MM-DD.
            - category: The category of the session or time (e.g., "Under 1 minute", "1-2 minutes").
            - total_distinct_device: The count of unique devices or users or sessions in the session category.
            - total_device: The total number of devices or users or sessions in the session category.
            - avg_duration: The average duration of sessions in minutes for this category.
            
            **Use this tool for all session-related queries** such as:
            1. Fetching general session statistics grouped by date and category.
            2. Identifying trends in session durations.
            3. Aggregating data for detailed session insights.
            4. Filtering session data based on specific categories or dates.
            `,
        parameters: {},
      },
      async () => {
        try {
          console.log("get_session_info");
    
          // Convert avg_duration to minutes for all session data
          const processedData = sessionData.map(session => ({
            ...session,
            avg_duration: (session.avg_duration / 60000).toFixed(2), // Convert ms to minutes
          }));
    
          return processedData;
        } catch (error) {
          console.error('Error processing JSON data:', error);
          throw new Error('Failed to return the session data.');
        }
      }
    );
    
    

    // handle realtime events from client + server for event logging
    client.on('realtime.event', (realtimeEvent: RealtimeEvent) => {
      setRealtimeEvents((realtimeEvents) => {
        const lastEvent = realtimeEvents[realtimeEvents.length - 1];
        if (lastEvent?.event.type === realtimeEvent.event.type) {
          // if we receive multiple events in a row, aggregate them for display purposes
          lastEvent.count = (lastEvent.count || 0) + 1;
          return realtimeEvents.slice(0, -1).concat(lastEvent);
        } else {
          return realtimeEvents.concat(realtimeEvent);
        }
      });
    });
    client.on('error', (event: any) => console.error(event));
    client.on('conversation.interrupted', async () => {
      const trackSampleOffset = await wavStreamPlayer.interrupt();
      if (trackSampleOffset?.trackId) {
        const { trackId, offset } = trackSampleOffset;
        await client.cancelResponse(trackId, offset);
      }
    });
    client.on('conversation.updated', async ({ item, delta }: any) => {
      const items = client.conversation.getItems();
      if (delta?.audio) {
        wavStreamPlayer.add16BitPCM(delta.audio, item.id);
      }
      if (item.status === 'completed' && item.formatted.audio?.length) {
        const wavFile = await WavRecorder.decode(
          item.formatted.audio,
          24000,
          24000
        );
        item.formatted.file = wavFile;
      }
      setItems(items);
    });

    setItems(client.conversation.getItems());

    return () => {
      // cleanup; resets to defaults
      client.reset();
    };
  }, []);

  /**
   * Render the application
   */
  return (

    <div className="page-content">
      <div data-component="ConsolePage">
        <div className="visualization" >
          <div className="visualization-entry client">
            <canvas ref={clientCanvasRef} />
          </div>
          <div className="visualization-entry server">
            <canvas ref={serverCanvasRef} />
          </div>
        </div>
        <div className="content-main">
          <div className="content-logs">
            <div className="content-block conversation">

              {/* Başlık sadece isTitleVisible true olduğunda gösterilir */}
              {isTitleVisible && (
                <div className="content-block-title">What Can I Help With?...</div>
              )}
              <div className="content-block-body" data-conversation-content>
                {items.map((conversationItem, i) => {
                  return (
                    <>
                      {conversationItem.role && ( // Eğer conversationItem.role mevcutsa render et
                        <div className="conversation-item" key={conversationItem.id}>
                          <div className={`speaker ${conversationItem.role}`}>
                            <div>
                              {conversationItem.role
                                .replaceAll('_', ' ')
                                .replace('assistant', 'AImee:')
                                .replace('user', 'You:')}
                            </div>
                          </div>
                          <div className="speaker-content">
                            {!conversationItem.formatted.tool && conversationItem.role === 'user' && (
                              <div>
                                {conversationItem.formatted.transcript ||
                                  (conversationItem.formatted.audio?.length
                                    ? '(awaiting transcript)'
                                    : conversationItem.formatted.text || '(item sent)')}
                              </div>
                            )}
                            {!conversationItem.formatted.tool && conversationItem.role === 'assistant' && (
                              <div className="message-body">
                                <ReactMarkdown>
                                  {conversationItem.formatted.transcript ||
                                    conversationItem.formatted.text ||
                                    '(truncated)'}
                                </ReactMarkdown>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  );

                })}
              </div>
            </div>
            <div className="content-actions">
              <Toggle
                defaultValue={false}
                labels={['Manual', 'Live']}
                values={['none', 'server_vad']}
                onChange={(_, value) => changeTurnEndType(value)}
              />
              <div className="spacer" />
              {isConnected && canPushToTalk && (
                <Button
                  label={isRecording ? 'Release to send' : 'Push to talk'}
                  buttonStyle={isRecording ? 'alert' : 'regular'}
                  disabled={!isConnected || !canPushToTalk}
                  onMouseDown={startRecording}
                  onMouseUp={stopRecording}
                />
              )}
              <div className="spacer" />
              <Button
                label={isConnected ? 'Disconnect' : 'Connect'}
                iconPosition={isConnected ? 'end' : 'start'}
                icon={isConnected ? X : Zap}
                buttonStyle={isConnected ? 'regular' : 'action'}
                onClick={
                  isConnected ? disconnectConversation : connectConversation
                }
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
export default ConsolePage;